
import React, { useState } from 'react';
import ProfessionStep from './ProfessionStep';
import FamilyStep from './FamilyStep';
import FinancialStep from './FinancialStep';
import ClimateStep from './ClimateStep';
import SpecialNeedsStep from './SpecialNeedsStep';
import RecommendationsStep from './RecommendationsStep';

interface DashboardLayoutProps {
  onBack: () => void;
  onHome: () => void;
  onExploreAi?: () => void;
  language: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ onBack, onHome, onExploreAi, language }) => {
  const [step, setStep] = useState<'profession' | 'family' | 'special_needs' | 'financial' | 'climate' | 'recommendations'>('profession');
  const [userChoices, setUserChoices] = useState<any>({
    profession: '',
    familySize: '',
    privateSchools: false,
    specialNeeds: false,
    specialNeedsDetail: null,
    financial: { planType: 'buy', range: '' },
    climate: []
  });

  const isRTL = language === 'ar';

  const translations: Record<string, any> = {
    en: {
      recStep: "STEP 5 OF 5",
      preferred: "Preferred City",
      alternative: "Alternative Option",
      downloadBtn: "Email Full Report",
      exploreBtn: "Explore more. Ask Settlo AI",
      errorTitle: "Generation Failed",
      errorBtn: "Retry Analysis",
      loadingTitle: "City Matching...",
      loadingDesc1: "AI retrieves and matches information from over 3,000 US cities based on your options. This may take 1-3 minutes, please be patient...",
      loadingDesc2: "Settlo Ai is a US immigration tool, operating based on legal constraints and official data.",
      prof: {
        step: "STEP 1 OF 5",
        title: "What is your main profession?",
        subtitle: "To find professional hubs that suit your lifestyle",
        tech: "Technology / IT",
        finance: "Finance / Investment",
        healthcare: "Healthcare / Bio",
        edu: "Education / Academia",
        energy: "Energy / Manufacturing",
        trade: "Trade",
        media: "Media / Arts",
        other: "Other",
        continue: "Continue"
      },
      family: {
        step: "STEP 2 OF 5",
        title: "Who will be relocating with you?",
        subtitle: "We prioritize schools and space based on your family size.",
        noChildren: "No Children",
        familyWithChildren: "Family with Children",
        privateSchools: "Considering Private Schools?",
        privateSchoolsSub: "Prioritize cities with elite resources.",
        specialNeeds: "Special Needs Support?",
        specialNeedsSub: "Prioritize specialized medical & education.",
        continue: "Continue"
      },
      specialNeeds: {
        step: "STEP 2.5 OF 5",
        title: "Support Assessment",
        subtitle: "Help us find the best support systems.",
        catLabel: "Category",
        neuro: "Neurodiversity",
        physical: "Physical Disability",
        pathLabel: "Path",
        path1: "K-12 Support",
        path1Sub: "Requires IEP Support",
        path2: "18+ Vocational",
        path2Sub: "Life Skills Training",
        continue: "Continue"
      },
      finance: {
        step: "STEP 3 OF 5",
        title: "Financial Plan",
        subtitle: "Housing is your largest expense.",
        buy: "Buy",
        rent: "Rent",
        valueFocus: "Value Focus",
        balanced: "Balanced",
        primeHubs: "Prime Hubs",
        luxury: "Luxury",
        whyTitle: "Ranges",
        buyInfo: [
          {label: "Median Price", desc: "Based on 2024 Market Data"},
          {label: "Down Payment", desc: "Estimated at 20% standard"},
          {label: "Mortgage Est.", desc: "Based on current APR rates"}
        ],
        rentInfo: [
          {label: "Standard FMR", desc: "Fair Market Rent index"},
          {label: "Security Dep.", desc: "Usually 1-2 months rent"},
          {label: "Utilities Est.", desc: "Power, Water, & Trash index"}
        ],
        continue: "Continue"
      },
      climate: {
        step: "STEP 4 OF 5",
        title: "Climate Preferences",
        subtitle: "Pick up to 2 environments.",
        flexible: "I'm Flexible",
        source: "Source: NOAA Climate Index",
        continue: "Continue",
        options: {
          spring: { title: "Eternal Spring", desc: "65-75°F year-round" },
          warm: { title: "Coastal Warmth", desc: "Mild winters" },
          dry: { title: "Dry Heat", desc: "Low humidity" },
          seasons: { title: "4 Seasons", desc: "Classic variety" },
          highland: { title: "Highland", desc: "Mountain air" },
          desert: { title: "Desert", desc: "Vibrant aridity" }
        }
      },
      cards: {
        career: { title: "Career & Economic Hub" },
        family: { title: "Family & Living Space" },
        housing: { title: "Real Estate Dynamics" },
        education: { title: "Educational Resources" },
        support: { title: "Specialized Support" },
        health: { title: "Healthcare Infrastructure" },
        climate: { title: "Environmental Profile" },
        culture: { title: "Cultural Ecosystem" },
        tax: { title: "Tax & Fiscal Policy" },
        safety: { title: "Safety & Security" },
        transport: { title: "Transport & Logistics" }
      }
    },
    zh: {
      recStep: "STEP 5 OF 5",
      preferred: "首选城市",
      alternative: "备选方案",
      downloadBtn: "Email Full Report",
      exploreBtn: "了解更多。问Settlo AI",
      errorTitle: "生成失败",
      errorBtn: "重试分析",
      loadingTitle: "城市匹配中...",
      loadingDesc1: "AI根据你的选项，检索匹配美国超过3000个城市资源信息，这可能需要1-3分钟，请耐心等待...",
      loadingDesc2: "Settlo Ai是一个美国移民工具，基于法律约束和官方数据运行",
      prof: {
        step: "STEP 1 OF 5",
        title: "您的主要职业是什么？",
        subtitle: "寻找适合您生活方式的职业中心",
        tech: "技术 / IT",
        finance: "金融 / 投资",
        healthcare: "医疗 / 生物",
        edu: "教育 / 学术",
        energy: "能源 / 制造",
        trade: "贸易",
        media: "媒体 / 艺术",
        other: "其他",
        continue: "继续"
      },
      family: {
        step: "STEP 2 OF 5",
        title: "谁将与您一起搬迁？",
        subtitle: "我们将根据您的家庭规模优先考虑学校和空间。",
        noChildren: "无子女",
        familyWithChildren: "带子女的家庭",
        privateSchools: "考虑私立学校？",
        privateSchoolsSub: "优先考虑拥有精英资源的城市。",
        specialNeeds: "特殊需求支持？",
        specialNeedsSub: "优先考虑专业医疗和教育。",
        continue: "继续"
      },
      specialNeeds: {
        step: "STEP 2.5 OF 5",
        title: "支持评估",
        subtitle: "帮助我们找到最好的支持系统。",
        catLabel: "类别",
        neuro: "神经多样性",
        physical: "肢体障碍",
        pathLabel: "路径",
        path1: "K-12 支持",
        path1Sub: "需要 IEP 支持",
        path2: "18+ 职业",
        path2Sub: "生活技能培训",
        continue: "继续"
      },
      finance: {
        step: "STEP 3 OF 5",
        title: "财务计划",
        subtitle: "住房是您最大的支出。",
        buy: "购房",
        rent: "租房",
        valueFocus: "注重性价比",
        balanced: "平衡型",
        primeHubs: "核心枢纽",
        luxury: "奢华",
        whyTitle: "范围",
        buyInfo: [
          {label: "中位数价格", desc: "基于 2024 年市场数据"},
          {label: "首付估算", desc: "按 20% 标准计算"},
          {label: "月供参考", desc: "基于当前 APR 利率估算"}
        ],
        rentInfo: [
          {label: "标准租金", desc: "公平市场租金指数 (FMR)"},
          {label: "安全押金", desc: "通常为 1-2 个月租金"},
          {label: "杂费估算", desc: "水电气及垃圾处理指数"}
        ],
        continue: "继续"
      },
      climate: {
        step: "STEP 4 OF 5",
        title: "气候偏好",
        subtitle: "最多选择 2 种环境。",
        flexible: "不限",
        source: "来源：NOAA 气候指数",
        continue: "继续",
        options: {
          spring: { title: "四季如春", desc: "全年 65-75°F" },
          warm: { title: "沿海温暖", desc: "冬季温和" },
          dry: { title: "干热", desc: "低湿度" },
          seasons: { title: "四季分明", desc: "经典变化" },
          highland: { title: "高原", desc: "山地空气" },
          desert: { title: "沙漠", desc: "充满活力的干旱" }
        }
      },
      cards: {
        career: { title: "职业与经济中心" },
        family: { title: "家庭与生活空间" },
        housing: { title: "房地产动态" },
        education: { title: "教育资源" },
        support: { title: "专业支持" },
        health: { title: "医疗基础设施" },
        climate: { title: "环境概况" },
        culture: { title: "文化生态系统" },
        tax: { title: "税务与财政政策" },
        safety: { title: "公共安全与治安" },
        transport: { title: "交通与物流" }
      }
    }
  };

  const t = translations[language] || translations.en;

  const handleProfessionContinue = (prof: string) => {
    setUserChoices((prev: any) => ({ ...prev, profession: prof }));
    setStep('family');
  };

  const handleFamilyContinue = (specialNeeds: boolean, size: string, privateSchools: boolean) => {
    setUserChoices((prev: any) => ({ ...prev, familySize: size, privateSchools, specialNeeds }));
    if (specialNeeds) {
      setStep('special_needs');
    } else {
      setStep('financial');
    }
  };

  const handleSpecialNeedsContinue = (detail: any) => {
    setUserChoices((prev: any) => ({ ...prev, specialNeedsDetail: detail }));
    setStep('financial');
  };

  const handleFinancialContinue = (fin: { planType: 'buy' | 'rent', range: string }) => {
    setUserChoices((prev: any) => ({ ...prev, financial: fin }));
    setStep('climate');
  };

  const handleClimateContinue = (clim: string[]) => {
    setUserChoices((prev: any) => ({ ...prev, climate: clim }));
    setStep('recommendations');
  };

  const renderStep = () => {
    switch (step) {
      case 'profession':
        return (
          <ProfessionStep 
            t={t.prof} 
            isRTL={isRTL} 
            language={language}
            onBack={onBack} 
            onHome={onHome}
            onContinue={handleProfessionContinue} 
          />
        );
      case 'family':
        return (
          <FamilyStep 
            t={t.family} 
            isRTL={isRTL} 
            language={language}
            onBack={() => setStep('profession')} 
            onHome={onHome}
            onContinue={handleFamilyContinue} 
          />
        );
      case 'special_needs':
        return (
          <SpecialNeedsStep 
            t={t.specialNeeds} 
            isRTL={isRTL} 
            language={language}
            onBack={() => setStep('family')} 
            onHome={onHome}
            onContinue={handleSpecialNeedsContinue} 
          />
        );
      case 'financial':
        return (
          <FinancialStep 
            t={t.finance} 
            isRTL={isRTL} 
            language={language}
            onBack={() => userChoices.specialNeeds ? setStep('special_needs') : setStep('family')} 
            onHome={onHome}
            onContinue={handleFinancialContinue} 
          />
        );
      case 'climate':
        return (
          <ClimateStep 
            t={t.climate} 
            isRTL={isRTL} 
            language={language}
            onBack={() => setStep('financial')} 
            onHome={onHome}
            onContinue={handleClimateContinue} 
          />
        );
      case 'recommendations':
        return (
          <RecommendationsStep 
            t={t} 
            isRTL={isRTL} 
            onBack={() => setStep('climate')} 
            onReset={onBack} 
            onHome={onHome}
            onExploreAi={onExploreAi}
            userChoices={userChoices} 
            language={language} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`relative min-h-screen w-full overflow-hidden bg-background-light text-slate-dark font-display ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Consistent Background Layer */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-background-light">
          <div className="absolute inset-0 origin-center" style={{ transform: 'rotate(-15deg) scale(1.5)' }}>
            <div className="absolute inset-0 grid-pattern opacity-30"></div>
          </div>
          <div className="aurora-blur"></div>
        </div>
      </div>

      <main className="relative z-10 flex-1 min-h-screen overflow-hidden">
        <div className="h-full w-full">
          {renderStep()}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
